import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AGENTS } from '../agents.js';
import AgentMark from '../components/AgentMark.jsx';
import { fetchSpec, drawSpec } from '../room-model.js';

// TheSpec - the deliverable, rendered from the approved Brief. A near-mirror of the
// One-pager viewer, pointed at the /spec endpoints. Readiness uses coreCovered +
// openBlocks rather than the decision-era settledCalls/openCalls.
export default function TheSpec({ projectId, onBackToDesk, onChanged }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [selectedV, setSelectedV] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [drawErr, setDrawErr] = useState('');
  const alive = useRef(true);

  useEffect(() => () => { alive.current = false; }, []);

  const reload = useCallback(async () => {
    const s = await fetchSpec(projectId);
    if (!alive.current) return;
    setData(s || { versions: [], readiness: null });
    setLoaded(true);
    if (onChanged) onChanged();
  }, [projectId, onChanged]);

  useEffect(() => {
    let ok = true;
    setSelectedV(null);
    fetchSpec(projectId)
      .then(s => { if (ok && alive.current) { setData(s || { versions: [], readiness: null }); setLoaded(true); } })
      .catch(() => { if (ok && alive.current) { setData({ versions: [], readiness: null }); setLoaded(true); } });
    return () => { ok = false; };
  }, [projectId]);

  async function handleDraw() {
    setDrawErr('');
    setDrawing(true);
    try {
      await drawSpec(projectId);
      setSelectedV(null);
    } catch (e) {
      if (e && e.status === 409) setDrawErr((e.body && e.body.reason) || 'Not ready to draw yet.');
      else setDrawErr('Could not draw the spec.');
    } finally {
      setDrawing(false);
      reload();
    }
  }

  if (!loaded || !data) return null;

  const versions = data.versions || [];
  const readiness = data.readiness || {};
  const stage = readiness.stage;
  const back = onBackToDesk
    ? <div style={{ marginTop: 14 }}><button type="button" className="btn ghost" onClick={onBackToDesk}>← Back to the desk</button></div>
    : null;

  if (stage === 'seeded') {
    return (
      <div className="op-early">
        <h2>Too early for the spec.</h2>
        <p>The Spec assembles itself from the approved Brief: the concept, the room map, the commercial shape. The room has only just had your seed. Give it a beat.</p>
        {back}
      </div>
    );
  }

  if (versions.length === 0) {
    const ready = !!readiness.ready;
    return (
      <div className="op-early">
        <h2>Not drawn up yet.</h2>
        <div className="op-locked-status">
          <span className={readiness.coreCovered ? 'ok' : 'open'}>{readiness.coreCovered ? 'CORE COVERED' : 'CORE THIN'}</span>
          <span style={{ color: 'var(--ink-4)' }}>/</span>
          <span className={readiness.openBlocks ? 'open' : 'ok'}>{readiness.openBlocks || 0} BLOCKING</span>
        </div>
        <p>The Spec is drawn deliberately from the Brief, once the concept and the room map have earned it. Approve the core proposals on the Brief and settle any blocking calls, and this activates.</p>
        <button type="button" className={`btn ${ready ? 'accent' : ''}`} disabled={!ready || drawing} onClick={handleDraw}>
          {drawing ? 'Drawing up…' : 'Draw up the spec'}
        </button>
        {drawErr && <div style={{ marginTop: 12, color: 'var(--accent-ink)', fontSize: 12 }}>{drawErr}</div>}
        {back}
      </div>
    );
  }

  const latest = versions[versions.length - 1];
  const latestN = latest.v;
  const shownN = selectedV == null ? latestN : selectedV;
  const shown = versions.find(v => v.v === shownN) || latest;
  const isLatestShown = shown.v === latestN;
  const canRedraw = stage === 'ready-to-redraw';
  const moved = stage === 'moved' || stage === 'ready-to-redraw';

  return (
    <div className="opd">
      <main className="onepager-doc">
        <div className="ver-bar">
          <div className="ver-pills">
            {versions.map(v => {
              const on = v.v === shownN;
              const tag = v.v === latestN ? ' · CURRENT' : ' · SUPERSEDED';
              return (
                <button key={v.v} type="button" className={on ? 'on' : ''} onClick={() => setSelectedV(v.v === latestN ? null : v.v)}>
                  V{v.v}{tag}
                </button>
              );
            })}
          </div>

          {moved && isLatestShown && (
            <div className="moved-banner">
              <span className="mb-text">The Brief has moved since v{latestN}. {canRedraw ? 'The new canon is settled and this page is behind the facts.' : 'Approve the new canon to redraw.'}</span>
              <button type="button" className={`btn small ${canRedraw ? 'accent' : ''}`} disabled={!canRedraw || drawing} onClick={handleDraw}>
                {drawing ? 'Redrawing…' : 'Redraw → v' + (latestN + 1)}
              </button>
            </div>
          )}
          {!isLatestShown && (
            <div className="moved-banner superseded">
              <span className="mb-text">You are reading v{shown.v}, superseded by v{latestN}. Kept for the record.</span>
            </div>
          )}
          {drawErr && <div style={{ marginTop: 10, color: 'var(--accent-ink)', fontSize: 12 }}>{drawErr}</div>}
        </div>

        <div className="op-kicker">
          <span className="mono-label">THE SPEC · V{shown.v}{isLatestShown ? '' : ' · SUPERSEDED'}</span>
        </div>

        {Array.isArray(shown.changelog) && shown.changelog.length > 0 && (
          <div className="op-changelog">
            <div className="mono-label" style={{ marginBottom: 8 }}>WHAT CHANGED</div>
            {shown.changelog.map((c, i) => <div className="oc-row" key={i}>{c}</div>)}
          </div>
        )}

        {(shown.sections || []).map(sec => (
          <section className="op-section" id={`sec-${sec.id}`} key={sec.id}>
            <div className="sec-head" style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <h3 className="sec-t" style={{ margin: 0 }}>{sec.title}</h3>
            </div>
            <div className="sec-body">
              {(sec.paras || []).map((p, i) => <p key={i}>{p}</p>)}
              {Array.isArray(sec.bullets) && sec.bullets.length > 0 && (
                <ul>{sec.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
              )}
            </div>
            {Array.isArray(sec.provenance) && sec.provenance.length > 0 && (
              <div className="sec-prov" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {sec.provenance.map((p, i) => {
                  const a = AGENTS[p.agent];
                  return (
                    <span key={`${p.agent}-${i}`} className="prov-chip" style={{ cursor: 'default' }}>
                      <AgentMark agent={a} size="xs" />
                      <span>{(a ? a.name : p.agent).toUpperCase()}</span>
                    </span>
                  );
                })}
              </div>
            )}
          </section>
        ))}

        {back}
      </main>
    </div>
  );
}
